import { call, put, takeLatest, all, takeEvery, select } from 'redux-saga/effects';
import { push } from 'react-router-redux';
import { toast } from 'react-toastify';
import {
  searchOrders,
  getOrderDetail,
  createOrder,
  modifyOrder,
  updateOrderStatus,
  getSubscriptionByAccountId,
  getSubscriptionAssets,
  getServiceUnitsBySubscriptionId,
  getPriceUnitsByServiceUnitId,
  getServiceUnitsByAccountId,
  getBundleDetail,
  getDependentOffers,
  getOmsConfig,
  modifyOmsConfig,
  createOmsConfig,
  getProvSequenceConfig,
  modifyProvSequenceConfig,
  createProvSequenceConfig,
  getFlexAttributesMapping,
  modifyFlexAttributeMapping,
  createFlexAttributeMapping,
  createBillingAccounts,
  processSplitOrder,
  getOrderReasonCodes,
  modifyOrderReasonCodes,
  createOrderReasonCodes,
  getOrderSplitOpportunity,
  saveSplitOpportunity,
  undoProcessSplitOrder,
  searchProvisioningAttributesConfig,
  modifyProvisioningAttributesConfig,
  createProvisioningAttributesConfig,
  buildOrderProvisioningAttributes,
  getGatewayApiMapByName,
  getOrderOmsTasks,
  getOrderProvisioningOutboundTasks,
  getOrderProvisioningInboundTasks,
  processAccountAndOrder,
  getOrderAudit,
  convertJsonToOrder,
} from '../../api';
import { Messages } from '../../constantsApp';
import { formatStringUrl } from '../../utils/utils';
import { queryRequest, mutationRequest } from '../../utils/request';
import { isConnecting, isEndConnected } from '../Loader/actions';
import * as types from './types';
import * as actions from './actions';
import i18n from '../../i18n';
import { makeGetCcpPropertiesConfig } from '../App/selectors';

export function* searchOrdersSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const { page, size, filter, sort } = payload;
    const response = yield call(queryRequest, searchOrders({ page, size, filter, sort }));
    if (response.searchOrders) {
      yield put(actions.searchOrdersSuccess(response.searchOrders));
    } else {
      yield put(actions.searchOrdersFailure('Failed to Fetch data!'));
    }
    yield put(isEndConnected());
    if (cb) cb();
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb();
  }
}

export function* getOrderSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getOrderDetail(payload));
    if (response.getOrderById) {
      const newOrder = response.getOrderById;
      // if (newOrder.services && newOrder.services.length)
      //   newOrder.services = newOrder.services.map(val => {
      //     const { packageId, bundleId, ...rest } = val;
      //     let isPackage = false;
      //     let isBundle = false;
      //     let isPriceOffer = false;
      //     if (packageId) {
      //       isPackage = true;
      //     } else if (bundleId) {
      //       isBundle = true;
      //     } else {
      //       isPriceOffer = true;
      //     }
      //     return { ...rest, packageId, bundleId, isPackage, isBundle, isPriceOffer };
      //   });
      yield put(actions.getOrderDetailSuccess(newOrder));
      if (cb) cb({ success: true, data: response.getOrderById });
    } else {
      if (cb) cb({ success: false, data: null });
      yield put(actions.getOrderDetailFailure(formatStringUrl(Messages.noOrder, payload)));
    }
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    yield put(actions.getOrderDetailFailure(formatStringUrl(Messages.noOrder, payload)));
  }
}

export function* createOrderSaga({ payload, cb = () => {} }) {
  const { isFromCreate, ...newPayload } = payload;
  yield put(isConnecting());
  try {
    const listProperties = yield select(makeGetCcpPropertiesConfig());
    let isOrderWrapper = false;
    let isEliminateFirstInvoice = false;
    if (listProperties && listProperties.length) {
      const accountOrderWrapperAPI = listProperties.find(val => val.property === 'accountOrderWrapperAPI');
      isOrderWrapper = accountOrderWrapperAPI && accountOrderWrapperAPI.value === 'true';
      const efi = listProperties.find(val => val.property === 'eliminateFirstInvoice');
      isEliminateFirstInvoice = efi && efi.value === 'true';
    }

    /* ISP-285 (Saray confirmed 2026-07-01 Path A): under eliminateFirstInvoice,
     * when the wizard has picked a reference order (correlationId is set), the
     * user's intent is to COMPLETE the existing reference order in place — not
     * create a new one. Same semantics as opening the reference in Order Detail,
     * filling the provisioning modal, and clicking Submit.
     *
     * Redirect the wizard's CREATE to a 2-step sequence on the reference order:
     *   1. modifyOrder(id=correlationId, services=[...], provisioningAttributes=[...])
     *      → persists the wizard's provisioning data onto the reference order
     *   2. updateOrderStatus(id=correlationId, status=SUBMITTED)
     *      → transitions CREATED → SUBMITTED → PROVISIONING_INITIATED
     *
     * Wizard flow WITHOUT a picked reference (fresh customer, no correlationId)
     * falls through to the standard processAccountAndOrder/createOrder path. */
    const referenceOrderId = newPayload.correlationId;
    if (isEliminateFirstInvoice && referenceOrderId) {
      /* Step 1: modifyOrder to save wizard's data onto the existing reference.
       *
       * Full schema audit performed 2026-07-01 to prevent whack-a-mole "unknown
       * field / missing field" errors. Each nested input's schema is at
       * core/service-transactional/src/main/resources/graphql/customerHub/orderManagement/inputs/
       *
       * Strategy: build each object EXPLICITLY from known-schema fields only.
       * No `...svc` spreads (which carry the wizard's bookkeeping fields like
       * `id: null`, `correlationId`, groupServices refs into the mutation).
       *
       * Enum-vs-string collision workaround: parseOrderMutation.js has a flat
       * `orderNS` key whitelist for enum serialization. `type` is on it (because
       * Order.type is an enum), but ModifyOrderServiceProvisionsInput.type is a
       * String. When we pass a plain "DEVICE" the serializer emits `type: DEVICE`
       * (invalid). Pre-wrap in quotes so the serializer's `${value}` fires with
       * `type: "DEVICE"` producing a valid String literal. */

      // Whitelist per ModifyOrderLinesInput schema
      const sanitizeLine = (ln, idx) => ({
        index: ln.index != null ? ln.index : idx + 1,
        action: ln.action || null,
        status: ln.status || null,
        reason: ln.reason || null,
        quantity: ln.quantity != null ? ln.quantity : null,
        priceOverride: ln.priceOverride != null ? ln.priceOverride : null,
        priceOffset: ln.priceOffset != null ? ln.priceOffset : null,
        discountPercent: ln.discountPercent != null ? ln.discountPercent : null,
        startDate: ln.startDate || null,
        endDate: ln.endDate || null,
        relativeStart: ln.relativeStart != null ? ln.relativeStart : null,
        relativeEnd: ln.relativeEnd != null ? ln.relativeEnd : null,
        relativeStartUnit: ln.relativeStartUnit || null,
        relativeEndUnit: ln.relativeEndUnit || null,
        priceOfferId: ln.priceOfferId || null,
        discountOfferId: ln.discountOfferId || null,
        instanceId: ln.instanceId || null,
        discAmountOverride: ln.discAmountOverride != null ? ln.discAmountOverride : null,
        discPercentOverride: ln.discPercentOverride != null ? ln.discPercentOverride : null,
      });

      // Whitelist per ModifyOrderServiceProvisionsInput schema
      const sanitizeProvisioningEntry = (p, idx) => {
        const rawType = p.type || p.dataType || null;
        return {
          index: p.index != null ? p.index : idx + 1,
          provisioningId: p.provisioningId || null,
          // Pre-quoted so the enum-emit serialization path outputs a String literal
          type: rawType ? `"${rawType}"` : null,
          instanceId: p.instanceId || null,
          effectiveDate: p.effectiveDate || null,
          endDate: p.endDate || null,
          flexAttr1: p.flexAttr1 || null,
          flexAttr2: p.flexAttr2 || null,
          flexAttr3: p.flexAttr3 || null,
          flexAttr4: p.flexAttr4 || null,
          flexAttr5: p.flexAttr5 || null,
          flexAttr6: p.flexAttr6 || null,
          flexAttr7: p.flexAttr7 || null,
          flexAttr8: p.flexAttr8 || null,
          flexAttr9: p.flexAttr9 || null,
          flexAttr10: p.flexAttr10 || null,
          // flexAttr11..25 must be carried too: iptvPlatform (MOTV vs HIBOX) is
          // flexAttr22, moTvProductId is flexAttr23. Capping at 10 dropped the
          // platform, routing MOTV accounts to HIBOX (ISP-285 provisioning submit).
          flexAttr11: p.flexAttr11 || null,
          flexAttr12: p.flexAttr12 || null,
          flexAttr13: p.flexAttr13 || null,
          flexAttr14: p.flexAttr14 || null,
          flexAttr15: p.flexAttr15 || null,
          flexAttr16: p.flexAttr16 || null,
          flexAttr17: p.flexAttr17 || null,
          flexAttr18: p.flexAttr18 || null,
          flexAttr19: p.flexAttr19 || null,
          flexAttr20: p.flexAttr20 || null,
          flexAttr21: p.flexAttr21 || null,
          flexAttr22: p.flexAttr22 || null,
          flexAttr23: p.flexAttr23 || null,
          flexAttr24: p.flexAttr24 || null,
          flexAttr25: p.flexAttr25 || null,
        };
      };

      // Whitelist per ModifyOrderServicesInput schema
      const sanitizeService = (svc, idx) => ({
        index: svc.index != null ? svc.index : idx + 1,
        serviceType: svc.serviceType || null,
        provisioningId: svc.provisioningId || null,
        action: svc.action || null,
        reason: svc.reason || null,
        status: svc.status || null,
        bundleId: svc.bundleId || null,
        packageId: svc.packageId || null,
        subscriptionName: svc.subscriptionName || null,
        instanceId: svc.instanceId || null,
        lines: Array.isArray(svc.lines) ? svc.lines.map(sanitizeLine) : null,
        provisioningList: Array.isArray(svc.provisioningList) && svc.provisioningList.length
          ? svc.provisioningList.map(sanitizeProvisioningEntry)
          : null,
      });

      // Whitelist per ModifyOrderSubscriptionsInput schema
      const sanitizeSubscription = (sub, idx) => ({
        index: sub.index != null ? sub.index : idx + 1,
        subscriptionName: sub.subscriptionName || null,
        initialTerm: sub.initialTerm != null ? sub.initialTerm : null,
        initialTermUnit: sub.initialTermUnit || null,
        renewalTerm: sub.renewalTerm != null ? sub.renewalTerm : null,
        renewalTermUnit: sub.renewalTermUnit || null,
        trialTerm: sub.trialTerm != null ? sub.trialTerm : null,
        trialTermUnit: sub.trialTermUnit || null,
        trialType: sub.trialType || null,
        trialToPaidNow: sub.trialToPaidNow != null ? sub.trialToPaidNow : null,
        subscriptionCategory: sub.subscriptionCategory || null,
      });

      /* Sanitize per OrderProvisioningAttributesInput + OrderProvisioningAttributesListInput schema.
       * These carry the pricing-derived template values (provisioningTemplate=S_INTERNET, speed=100Mbps, etc.)
       * that provision_gateway substitutes into the Nokia SOAP body. Without them, the SOAP goes out with
       * literal --currentTemplate-- placeholders (Saray saw this on 2026-07-01). */
      const sanitizeAttributeListEntry = (a, idx) => ({
        index: a.index != null ? a.index : idx + 1,
        referenceIndex: a.referenceIndex != null ? a.referenceIndex : null,
        name: a.name || null,
        value: a.value != null ? a.value : (a.defaultValue != null ? a.defaultValue : null),
      });
      const sanitizeProvisioningAttribute = (pa, idx) => ({
        index: pa.index != null ? pa.index : idx + 1,
        serviceIndex: pa.serviceIndex != null ? pa.serviceIndex : null,
        serviceType: pa.serviceType || null,
        provisioningName: pa.provisioningName || null,
        priceOfferId: pa.priceOfferId || null,
        provisioningAttributesList: Array.isArray(pa.provisioningAttributesList) && pa.provisioningAttributesList.length
          ? pa.provisioningAttributesList.map(sanitizeAttributeListEntry)
          : null,
      });

      const sanitizedServices = (newPayload.services || []).map(sanitizeService);

      /* ISP-285 (2026-07-04, Saray/Luis confirmed): LEGACY-LEFTOVER branch.
       *
       * Two kinds of reference order can be picked under eliminateFirstInvoice:
       *   (a) CREATED work order  → the NEW-flow account. Complete it in place
       *       via Path A modify (below). Saray: "the order should be the same".
       *   (b) COMPLETED billing-only first-invoice order → an OLD account created
       *       before the tenant switched to eliminateFirstInvoice. It already
       *       paid its first invoice and still has the FIRST_INVOICE_SUBSCRIPTION
       *       dummy, but was never provisioned. Path A CANNOT complete it —
       *       engine validateModify rejects a COMPLETED order ("Only Pending/
       *       Created/Failed orders can be modified", which Saray hit on acct
       *       2025066). For these, replicate the pre-ISP-285 CRM "second order"
       *       call: processAccountAndOrder with a NEW order carrying
       *       prevOrderReferenceId = reference order id. The engine
       *       (PGAccountAndOrderProcessService.processOrder →
       *       processFirstInvoiceSecondOrder, gate relaxed to fire under
       *       eliminateFirstInvoice in the same ticket) then cancels the dummy,
       *       rebuilds the package from the first order under a real DEFAULT
       *       subscription, provisions using the device data we pass in
       *       provisioningList, and bills from the provisioning date forward with
       *       the already-paid period waived (billOrderCharges=false — NOT
       *       provisioningOnly, so go-forward billing still happens per Saray).
       *
       * Discriminate by the reference order's status (fetch it fresh — don't
       * trust stale wizard state). */
      let referenceOrderStatus = null;
      try {
        const refResp = yield call(queryRequest, getOrderDetail(referenceOrderId));
        referenceOrderStatus = refResp && refResp.getOrderById ? refResp.getOrderById.status : null;
      } catch (e) {
        referenceOrderStatus = null;
      }

      if (referenceOrderStatus === 'COMPLETED') {
        /* Minimal second-order services: serviceType + provisioningList carrying
         * the NOC-entered device data. The engine rebuilds the full package
         * (lines/prices/attributes) from the reference order, so we only need the
         * device data here.
         *
         * BOTH provisioningId AND type are mandatory on each provisioningList
         * entry: validateNewOrder (PGAccountAndOrderProcessService:1028-1040)
         * throws PROVISIONING_ID_AND_TYPE_MANDATORY otherwise, and that check runs
         * BEFORE the DEVICE-defaulting inside processFirstInvoiceSecondOrder — so
         * we cannot rely on the engine to fill type. Pass it as `dataType`:
         * processAccountAndOrder.js renames dataType->type in the serialized
         * mutation, yielding a quoted String literal (provisioning `type` is a
         * String on the input schema), while the order-level `type: NEW` stays an
         * unquoted enum. Sending `type` directly here would serialize as the enum
         * `type: DEVICE` (invalid String) — hence the dataType indirection, same
         * channel the legacy CRM second-order call used. */
        const secondOrderServices = (newPayload.services || []).map((svc, idx) => ({
          index: svc.index != null ? svc.index : idx + 1,
          serviceType: svc.serviceType || null,
          action: svc.action || 'ADD',
          provisioningList:
            Array.isArray(svc.provisioningList) && svc.provisioningList.length
              ? svc.provisioningList.map((p, i) => ({
                  index: p.index != null ? p.index : i + 1,
                  provisioningId: p.provisioningId || null,
                  dataType: p.type || p.dataType || 'DEVICE',
                  // Carry the NOC-entered device attrs (OntModel etc. land in
                  // flexAttrs via the Ver modal's flexAttrMapping). The engine
                  // merges this provisioningList verbatim onto the rebuilt order
                  // (PGAccountAndOrderProcessService:646), so anything dropped
                  // here is lost to the Nokia SOAP body downstream.
                  flexAttr1: p.flexAttr1 || null,
                  flexAttr2: p.flexAttr2 || null,
                  flexAttr3: p.flexAttr3 || null,
                  flexAttr4: p.flexAttr4 || null,
                  flexAttr5: p.flexAttr5 || null,
                  flexAttr6: p.flexAttr6 || null,
                  flexAttr7: p.flexAttr7 || null,
                  flexAttr8: p.flexAttr8 || null,
                  flexAttr9: p.flexAttr9 || null,
                  flexAttr10: p.flexAttr10 || null,
                  // flexAttr11..25: iptvPlatform=flexAttr22 (MOTV/HIBOX selector),
                  // moTvProductId=flexAttr23. Capping at 10 dropped the platform.
                  flexAttr11: p.flexAttr11 || null,
                  flexAttr12: p.flexAttr12 || null,
                  flexAttr13: p.flexAttr13 || null,
                  flexAttr14: p.flexAttr14 || null,
                  flexAttr15: p.flexAttr15 || null,
                  flexAttr16: p.flexAttr16 || null,
                  flexAttr17: p.flexAttr17 || null,
                  flexAttr18: p.flexAttr18 || null,
                  flexAttr19: p.flexAttr19 || null,
                  flexAttr20: p.flexAttr20 || null,
                  flexAttr21: p.flexAttr21 || null,
                  flexAttr22: p.flexAttr22 || null,
                  flexAttr23: p.flexAttr23 || null,
                  flexAttr24: p.flexAttr24 || null,
                  flexAttr25: p.flexAttr25 || null,
                }))
              : null,
        }));

        const existingCustomAttrs = Array.isArray(newPayload.orderCustomAttributes)
          ? newPayload.orderCustomAttributes.filter(a => a && a.name !== 'prevOrderReferenceId')
          : [];

        const secondOrderPayload = {
          order: {
            type: newPayload.type || 'NEW',
            accountId: newPayload.accountId,
            userId: newPayload.userId,
            effectiveDate: newPayload.effectiveDate || null,
            services: secondOrderServices,
            orderCustomAttributes: [
              ...existingCustomAttrs,
              { name: 'prevOrderReferenceId', value: referenceOrderId },
            ],
          },
        };

        const secondResp = yield call(mutationRequest, processAccountAndOrder(secondOrderPayload));
        const newOrderId =
          secondResp && secondResp.processAccountAndOrder
            ? secondResp.processAccountAndOrder.orderId
            : null;
        if (!newOrderId) {
          // engine surfaced its own error toast via mutationRequest — stop here.
          yield put(isEndConnected());
          cb({});
          return;
        }
        yield put(actions.createOrderSuccess());
        yield put(isEndConnected());
        yield put(push(`/orders/${newOrderId}/detail`));
        yield cb({ success: true, id: newOrderId });
        toast.success(i18n.t('common:apiMessage.createOrderSuccessfully'));
        return;
      }

      /* Deliberately DO NOT send subscriptions in the wizard modifyOrder payload.
       *
       * Reason: engine's PGOrderSubscriptionsService.modify (line 120+) branches
       * to a private `create(input)` invocation when the order has no existing
       * subscription row yet (which is the case for ISP-285 fresh reference
       * orders — Coope CRM creates the order without subscriptions per the
       * eliminateFirstInvoice design). But `create(input)` is a 1-arg call in
       * Groovy that ambiguously resolves to `DSLContext.create.call(input)` —
       * DefaultDSLContext has no such method for OrderSubscriptions arg,
       * producing the "No signature of method" runtime error Saray saw
       * (2026-07-02).
       *
       * The actual 2-arg method `create(input, OrderType)` at line 64 IS what
       * the author intended, but the modify() method doesn't have OrderType in
       * scope. This is a pre-existing engine bug that predates ISP-285 — fixing
       * it would need cross-flow regression testing beyond this ticket's scope.
       *
       * Under ISP-285 wizard flow we don't need to modify subscriptions at all:
       * subscriptions get created downstream when provisioning completes via
       * PGOmsService.submitOrder → subscriptionService.create. The wizard's
       * only job here is (a) persist provisioning data onto the reference
       * order's services, then (b) transition to SUBMITTED. Both work with
       * subscriptions=null. */
      const suppressedSubscriptions = null;
      const sanitizedSubscriptions = suppressedSubscriptions;
      if (Array.isArray(newPayload.subscriptions) && newPayload.subscriptions.length) {
        // silence-safe: log which subscription indexes we're dropping so the
        // decision is visible in browser console during test.
        // eslint-disable-next-line no-console
        console.info('ISP-285 wizard: suppressing subscriptions from modifyOrder payload',
          newPayload.subscriptions.map((s, i) => ({ index: s.index || (i + 1), name: s.subscriptionName })));
      }

      /* Engine PGOrderService.modifyOrder line 886-931 gate:
       * The outer `if` triggers when userId is truthy (line 890). Once inside,
       * it builds `UPDATE order SET ...` but the userId SET clause is
       * COMMENTED OUT at line 900-901 (per EP-5789 audit retention). If our
       * payload contains userId (required by schema!) but no OTHER outer-
       * condition field, jOOQ generates UPDATE with no SET clauses, returns
       * null, and modelMapper.map(null, OrderEntity) throws "source cannot be
       * null" at line 930.
       *
       * Fix: add `reason` — it IS one of the outer-condition fields AND it
       * DOES have an active SET clause at line 898. This ensures at least one
       * column gets updated, jOOQ returns a valid record, modelMapper maps it,
       * modifyOrder proceeds. Value doubles as an audit trail — future readers
       * can see this order was completed via the ISP-285 wizard. */
      const sanitizedProvisioningAttributes = Array.isArray(newPayload.provisioningAttributes) && newPayload.provisioningAttributes.length
        ? newPayload.provisioningAttributes.map(sanitizeProvisioningAttribute)
        : null;

      const modifyPayload = {
        id: referenceOrderId,
        userId: newPayload.userId,
        type: newPayload.type,
        reason: 'ISP-285 wizard provisioning completion',
        services: sanitizedServices,
        subscriptions: sanitizedSubscriptions,
        // ISP-285: attach provisioningAttributes so engine populates
        // order_provisioning_attributes rows on the reference order. Without
        // them, provision_gateway's Nokia SOAP template can't resolve
        // currentTemplate (comes from provisioningTemplate attribute value)
        // and sends garbage placeholders to the OLT. Saray hit this on ORD2171
        // (2026-07-01) — Nokia rejected with ORA-01403.
        provisioningAttributes: sanitizedProvisioningAttributes,
        orderCustomAttributes: Array.isArray(newPayload.orderCustomAttributes) && newPayload.orderCustomAttributes.length
          ? newPayload.orderCustomAttributes
          : null,
      };
      const modifyResponse = yield call(mutationRequest, modifyOrder(modifyPayload));
      if (!(modifyResponse && modifyResponse.modifyOrder && modifyResponse.modifyOrder.id)) {
        // modifyOrder failed — engine's exception handler will have surfaced a
        // toast via mutationRequest. Just stop here.
        yield put(isEndConnected());
        cb({});
        return;
      }

      // Step 2: transition the reference order CREATED → SUBMITTED
      // QE H1: check response — must not report success if the transition
      // fails, otherwise NOC gets a green toast + navigates away while the
      // order stays in CREATED and provisioning never fires.
      const statusResponse = yield call(mutationRequest, updateOrderStatus({
        id: referenceOrderId,
        status: 'SUBMITTED',
      }));
      if (!(statusResponse && statusResponse.updateOrderStatus && statusResponse.updateOrderStatus.id)) {
        yield put(isEndConnected());
        cb({});
        return;
      }

      yield put(actions.createOrderSuccess());
      yield put(isEndConnected());
      yield put(push(`/orders/${referenceOrderId}/detail`));
      yield cb({ success: true, id: referenceOrderId });
      toast.success(i18n.t('common:apiMessage.createOrderSuccessfully'));
      return;
    }

    const apiCreaete = isOrderWrapper ? processAccountAndOrder : createOrder;
    const dataCreate = isOrderWrapper ? { order: newPayload } : newPayload;
    const response = yield call(mutationRequest, apiCreaete(dataCreate));
    yield put(actions.createOrderSuccess());

    yield put(isEndConnected());
    if (response.createOrder && response.createOrder.id && !isOrderWrapper) {
      yield put(push(`/orders/${response.createOrder.id}/detail`, { data: response.createOrder, isFromCreate }));
      yield cb({ success: true, id: response.createOrder.id });
      toast.success(i18n.t('common:apiMessage.createOrderSuccessfully'));
    }
    if (response.processAccountAndOrder && response.processAccountAndOrder.orderId && isOrderWrapper) {
      yield put(push(`/orders/${response.processAccountAndOrder.orderId}/detail`));
      yield cb({ success: true, id: response.processAccountAndOrder.orderId });
      toast.success(i18n.t('common:apiMessage.createOrderSuccessfully'));
    }
  } catch (err) {
    yield put(isEndConnected());
    // toast(err.message ? err.message : 'Order created failed!', { type: toast.TYPE.ERROR });
    cb({});
  }
}

export function* getServiceUnitsAcSaga({ payload, cb }) {
  try {
    const response = yield call(queryRequest, getServiceUnitsByAccountId(payload));
    if (response.getServiceUnitsByAccountId) {
      if (cb) {
        cb({ success: true, data: response.getServiceUnitsByAccountId });
      }
    } else {
      cb({ success: false, data: response && response.getServiceUnitsByAccountId });
    }
  } catch (err) {
    cb({ success: false, data: '' });
  }
}

export function* modifyOrderSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const dataCreate = payload;
    const response = yield call(mutationRequest, modifyOrder(dataCreate));
    yield put(actions.modifyOrderSuccess(response.modifyOrder));
    yield cb({ success: true });
    yield put(isEndConnected());
    toast.success(i18n.t('common:apiMessage.modifyOrderSuccessfully'));
  } catch (err) {
    yield put(isEndConnected());
    // toast(`Order modify failed! ${err}`, { type: toast.TYPE.ERROR });
    cb({ success: false });
  }
}

export function* updateOrderStatusSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, updateOrderStatus(payload));
    if (response.updateOrderStatus) {
      yield put(actions.updateOrderStatusSuccess(response.updateOrderStatus));
      if (cb) yield cb({ success: true });
      yield put(isEndConnected());
      toast.success(i18n.t('common:apiMessage.updateOrderStatusSuccessfully'));
    }
  } catch (err) {
    yield put(isEndConnected());
    // toast("Order's status updated failed!", { type: toast.TYPE.ERROR });
    if (cb) cb({ success: false });
  }
}

export function* getSubscriptionByAccountIdSaga({ payload, cb = () => {}, isNoGetAsset }) {
  yield put(isConnecting());

  try {
    yield put(actions.getSubscriptionAssetsSuccess([]));
    const response = yield call(queryRequest, getSubscriptionByAccountId(payload));
    if (response.getSubscriptionByAccountId) {
      if (cb) cb(true, response.getSubscriptionByAccountId);
      yield put(actions.getSubscriptionByAccountIdSuccess(response.getSubscriptionByAccountId));

      // yield response.getSubscriptionByAccountId.map(({ id }) => {
      //   console.info('id', id);
      //   return put(actions.getServiceUnitsBySubscriptionId(id));
      // });
      if (response.getSubscriptionByAccountId && response.getSubscriptionByAccountId.length) {
        if (
          !isNoGetAsset ||
          (isNoGetAsset &&
            response.getSubscriptionByAccountId.length &&
            response.getSubscriptionByAccountId.filter(val => val.status !== 'CLOSED').length === 1)
        ) {
          yield all(
            response.getSubscriptionByAccountId.map(({ id, status }) => {
              if (status === 'CLOSED') return null;
              return put(actions.getSubscriptionAssets({ id, status }));
            })
          );
        }
      }
    } else {
      const message = `Can not get subscription with account id ${payload}`;
      if (cb) cb(false, message);
      yield put(actions.getSubscriptionByAccountIdFailure(`Can not get subscription with account id ${payload}`));
    }
    yield put(isEndConnected());
  } catch (err) {
    const message = `Can not get subscription with account id ${payload}`;
    if (cb) cb(false, message);
    yield put(isEndConnected());
    yield put(actions.getSubscriptionByAccountIdFailure(`Can not get subscription with account id ${payload}`));
  }
}

export function* getSubscriptionAssetsSaga({ payload }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getSubscriptionAssets(payload));
    if (response.getSubscriptionAssets) {
      yield put(actions.getSubscriptionAssetsSuccess(response.getSubscriptionAssets));
    } else {
      yield put(
        actions.getSubscriptionAssetsFailure(`Can not get subscription assets with subscription id ${payload}`)
      );
    }
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    yield put(actions.getSubscriptionAssetsFailure(`Can not get subscription assets with subscription id ${payload}`));
  }
}

export function* getServiceUnitsBySubscriptionIdSaga({ payload }) {
  try {
    const response = yield call(queryRequest, getServiceUnitsBySubscriptionId(payload));
    if (response.getServiceUnitsBySubscriptionId) {
      yield put(actions.getServiceUnitsBySubscriptionIdSuccess(response.getServiceUnitsBySubscriptionId));
    } else {
      yield put(
        actions.getServiceUnitsBySubscriptionIdFailure(`Can not get service units with subscription id ${payload}`)
      );
    }
  } catch (err) {
    yield put(
      actions.getServiceUnitsBySubscriptionIdFailure(`Can not get service units with subscription id ${payload}`)
    );
  }
}

export function* getPriceUnitsByServiceUnitIdSaga({ payload }) {
  try {
    const response = yield call(queryRequest, getPriceUnitsByServiceUnitId(payload));
    if (response.getPriceUnitsByServiceUnitId) {
      yield put(actions.getPriceUnitsByServiceUnitIdSuccess(response.getPriceUnitsByServiceUnitId));
    } else {
      yield put(actions.getPriceUnitsByServiceUnitIdFailure(`Can not get price units with service unit id ${payload}`));
    }
  } catch (err) {
    yield put(actions.getPriceUnitsByServiceUnitIdFailure(`Can not get price units with service unit id ${payload}`));
  }
}

export function* getBundleDetailSaga({ payload, cb }) {
  try {
    const response = yield call(queryRequest, getBundleDetail(payload));
    if (response.getBundleById) {
      if (cb) cb({ success: true, data: response.getBundleById });
    }
  } catch (err) {
    if (cb) cb({ success: false });
  }
}

export function* getDependentOffersSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getDependentOffers(payload));
    if (response.getDependentOffers) {
      if (cb) cb({ success: true, data: response.getDependentOffers });
    }
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false });
  }
}

export function* searchOmsConfigSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getOmsConfig(payload));
    if (response.getOmsConfig) {
      yield put(actions.searchOmsSuccess(response.getOmsConfig));
    }
    yield put(isEndConnected());
    if (cb) cb({ success: true, data: response.getOmsConfig });
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* createOmsConfigSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, createOmsConfig(payload));
    if (response.createOmsConfig) {
      if (cb) cb({ success: true, data: response.createOmsConfig });
      toast.success(i18n.t('common:apiMessage.createOrderSequenceSuccessfully'));
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* modifyOmsConfigSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, modifyOmsConfig(payload));
    if (response.modifyOmsConfig) {
      toast.success(i18n.t('common:apiMessage.modifyOrderSequenceSuccessfully'));
      if (cb) cb({ success: true, data: response.modifyOmsConfig });
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

// Provisioning Task Sequence
export function* searchProvisioningTaskConfigSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getProvSequenceConfig(payload));
    if (response.getProvSequenceConfig) {
      yield put(actions.searchProvisioningTaskSequenceSuccess(response.getProvSequenceConfig));
    }
    yield put(isEndConnected());
    if (cb) cb({ success: true, data: response.getProvSequenceConfig });
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* createProvisioningTaskConfigSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, createProvSequenceConfig(payload));
    if (response.createProvSequenceConfig) {
      if (cb) cb({ success: true, data: response.createProvSequenceConfig });
      toast.success(i18n.t('common:apiMessage.createProvisioningTaskSequenceSuccessfully'));
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* modifyProvisioningTaskConfigSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, modifyProvSequenceConfig(payload));
    if (response.modifyProvSequenceConfig) {
      toast.success(i18n.t('common:apiMessage.modifyProvisioningTaskSequenceSuccessfully'));
      if (cb) cb({ success: true, data: response.modifyProvSequenceConfig });
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

// Provisioning Mapping
export function* getFlexAttributesMappingSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getFlexAttributesMapping(payload));
    if (response.getFlexAttributesMapping) {
      yield put(actions.searchProvisioningTaskSequenceSuccess(response.getFlexAttributesMapping));
    }
    yield put(isEndConnected());
    if (cb) cb({ success: true, data: response.getFlexAttributesMapping });
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* createFlexAttributeMappingSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, createFlexAttributeMapping(payload));
    if (response.createFlexAttributeMapping) {
      if (cb) cb({ success: true, data: response.createFlexAttributeMapping });
      toast.success(i18n.t('common:apiMessage.createProvisioningMappingSuccessfully'));
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* modifyFlexAttributeMappingSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, modifyFlexAttributeMapping(payload));
    if (response.modifyFlexAttributeMapping) {
      toast.success(i18n.t('common:apiMessage.modifyProvisioningMappingSuccessfully'));
      if (cb) cb({ success: true, data: response.modifyFlexAttributeMapping });
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* createBillingAccountsSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, createBillingAccounts(payload));
    if (response.createBillingAccounts) {
      if (cb) cb({ success: true, data: response.createBillingAccounts });
      toast.success(i18n.t('common:apiMessage.createBillingAccountSuccessfully'));
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* processSplitOrderSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, processSplitOrder(payload));
    if (response.processSplitOrder) {
      if (cb) cb({ success: true, data: response.processSplitOrder });
      toast.success(i18n.t('common:apiMessage.splitOrderAccountSuccessfully'));
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

// Order Reason Code
export function* getOrderReasonCodesSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getOrderReasonCodes(payload));
    if (response.getOrderReasonCodes) {
      yield put(actions.getOrderReasonCodesSuccess(response.getOrderReasonCodes));
    }
    yield put(isEndConnected());
    if (cb) cb({ success: true, data: response.getOrderReasonCodes });
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* createOrderReasonCodesSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, createOrderReasonCodes(payload));
    if (response.createOrderReasonCodes) {
      if (cb) cb({ success: true, data: response.createOrderReasonCodes });
      toast.success(i18n.t('common:apiMessage.createOrderReasonCodesSuccessfully'));
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* modifyOrderReasonCodesSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, modifyOrderReasonCodes(payload));
    if (response.modifyOrderReasonCodes) {
      toast.success(i18n.t('common:apiMessage.modifyOrderReasonCodesSuccessfully'));
      if (cb) cb({ success: true, data: response.modifyOrderReasonCodes });
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* getOrderSplitOpportunitySaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getOrderSplitOpportunity(payload));
    if (response.getOrderSplitOpportunity) {
      if (cb) cb({ success: true, data: response.getOrderSplitOpportunity });
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
  }
}

export function* saveSplitOpportunitySaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, saveSplitOpportunity(payload));
    if (response.saveSplitOpportunity) {
      toast.success(i18n.t('common:apiMessage.saveSplitOpportunity'));
      if (cb) cb({ success: true, data: response.saveSplitOpportunity });
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* undoProcessSplitOrderSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, undoProcessSplitOrder(payload));
    if (response.undoProcessSplitOrder) {
      toast.success(i18n.t('common:apiMessage.undoProcessSplitOrder'));
      if (cb) cb({ success: true, data: response.undoProcessSplitOrder });
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

// Provisioning Attributes Config
export function* searchProvisioningAttributesConfigSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, searchProvisioningAttributesConfig(payload));
    yield put(isEndConnected());
    if (cb) cb({ success: true, data: response.searchProvisioningAttributesConfig });
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* createProvisioningAttributesConfigSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, createProvisioningAttributesConfig(payload));
    if (response.createProvisioningAttributesConfig) {
      if (cb) cb({ success: true, data: response.createProvisioningAttributesConfig });
      toast.success(i18n.t('common:apiMessage.createProvisioningAttributesConfigSuccessfully'));
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* modifyProvisioningAttributesConfigSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, modifyProvisioningAttributesConfig(payload));
    if (response.modifyProvisioningAttributesConfig) {
      toast.success(i18n.t('common:apiMessage.modifyProvisioningAttributesConfigSuccessfully'));
      if (cb) cb({ success: true, data: response.modifyProvisioningAttributesConfig });
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* buildOrderProvisioningAttributesSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(mutationRequest, buildOrderProvisioningAttributes(payload));
    if (response.buildOrderProvisioningAttributes) {
      if (cb) cb({ success: true, data: response.buildOrderProvisioningAttributes });
    } else if (cb) cb({ success: false, data: null });
    yield put(isEndConnected());
  } catch (err) {
    yield put(isEndConnected());
    if (cb) cb({ success: false, data: null });
  }
}

export function* getGatewayApiMapByNameSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getGatewayApiMapByName(payload));
    if (response.getGatewayApiMapByName) {
      if (cb) cb({ success: true, data: response.getGatewayApiMapByName });
    } else if (cb) cb({ success: false });
    yield put(isEndConnected());
  } catch (err) {
    if (cb) cb({ success: false });
    yield put(isEndConnected());
  }
}

export function* getOrderOmsTasksSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getOrderOmsTasks(payload));
    if (response.getOrderOmsTasks) {
      if (cb) cb({ success: true, data: response.getOrderOmsTasks });
    } else if (cb) cb({ success: false });
    yield put(isEndConnected());
  } catch (err) {
    if (cb) cb({ success: false });
    yield put(isEndConnected());
  }
}

export function* getOrderProvisioningOutboundTasksSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getOrderProvisioningOutboundTasks(payload));
    if (response.getOrderProvisioningOutboundTasks) {
      if (cb) cb({ success: true, data: response.getOrderProvisioningOutboundTasks });
    } else if (cb) cb({ success: false });
    yield put(isEndConnected());
  } catch (err) {
    if (cb) cb({ success: false });
    yield put(isEndConnected());
  }
}

export function* getOrderProvisioningInboundTasksSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getOrderProvisioningInboundTasks(payload));
    if (response.getOrderProvisioningInboundTasks) {
      if (cb) cb({ success: true, data: response.getOrderProvisioningInboundTasks });
    } else if (cb) cb({ success: false });
    yield put(isEndConnected());
  } catch (err) {
    if (cb) cb({ success: false });
    yield put(isEndConnected());
  }
}

export function* getOrderAuditSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, getOrderAudit(payload));
    if (response.getOrderAudit) {
      if (cb) cb({ success: true, data: response.getOrderAudit });
    } else if (cb) cb({ success: false });
    yield put(isEndConnected());
  } catch (err) {
    if (cb) cb({ success: false });
    yield put(isEndConnected());
  }
}

export function* convertJsonToOrderSaga({ payload, cb }) {
  yield put(isConnecting());
  try {
    const response = yield call(queryRequest, convertJsonToOrder(payload));
    if (response.convertJsonToOrder) {
      if (cb) cb({ success: true, data: response.convertJsonToOrder });
    } else if (cb) cb({ success: false });
    yield put(isEndConnected());
  } catch (err) {
    if (cb) cb({ success: false });
    yield put(isEndConnected());
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export default function* ordersSaga() {
  yield takeLatest(types.SEARCH_ORDERS, searchOrdersSaga);
  yield takeLatest(types.GET_ORDERS_DETAIL, getOrderSaga);
  yield takeLatest(types.CREATE_ORDERS, createOrderSaga);
  yield takeLatest(types.MODIFY_ORDER, modifyOrderSaga);
  yield takeLatest(types.UPDATE_ORDER_STATUS, updateOrderStatusSaga);
  yield takeLatest(types.GET_SUBSCRIPTION_BY_ACCOUNT_ID, getSubscriptionByAccountIdSaga);
  yield takeLatest(types.GET_SUBSCRIPTION_ASSETS, getSubscriptionAssetsSaga);
  yield takeEvery(types.GET_SERVICE_UNITS_BY_SUBSCRIPTION_ID, getServiceUnitsBySubscriptionIdSaga);
  yield takeLatest(types.GET_PRICE_UNITS_BY_SERVICE_UNIT_ID, getPriceUnitsByServiceUnitIdSaga);
  yield takeLatest(types.GET_SERVICE_UNITS_AC, getServiceUnitsAcSaga);
  yield takeEvery(types.GET_BUNDLE_BY_ID, getBundleDetailSaga);
  yield takeEvery(types.GET_DEPENDENT_OFFERS, getDependentOffersSaga);
  yield takeLatest(types.SEARCH_OMS_CONFIG, searchOmsConfigSaga);
  yield takeLatest(types.CREATE_OMS_CONFIG, createOmsConfigSaga);
  yield takeLatest(types.MODIFY_OMS_CONFIG, modifyOmsConfigSaga);
  yield takeLatest(types.SEARCH_PROVISIONING_CONFIG, searchProvisioningTaskConfigSaga);
  yield takeLatest(types.CREATE_PROVISIONING_CONFIG, createProvisioningTaskConfigSaga);
  yield takeLatest(types.MODIFY_PROVISIONING_CONFIG, modifyProvisioningTaskConfigSaga);
  yield takeLatest(types.GET_FLEX_ATTRIBUTE_MAPPING, getFlexAttributesMappingSaga);
  yield takeLatest(types.CREATE_FLEX_ATTRIBUTE_MAPPING, createFlexAttributeMappingSaga);
  yield takeLatest(types.MODIFY_FLEX_ATTRIBUTE_MAPPING, modifyFlexAttributeMappingSaga);
  yield takeLatest(types.CREATE_BILLING_ACCOUNTS, createBillingAccountsSaga);
  yield takeLatest(types.PRCESS_SPLIT_ORDER, processSplitOrderSaga);
  yield takeLatest(types.SEARCH_ORDER_REASON_CODE, getOrderReasonCodesSaga);
  yield takeLatest(types.CREATE_ORDER_REASON_CODE, createOrderReasonCodesSaga);
  yield takeLatest(types.MODIFY_ORDER_REASON_CODE, modifyOrderReasonCodesSaga);
  yield takeLatest(types.GET_ORDER_SPLIT_OPPORTUNITY, getOrderSplitOpportunitySaga);
  yield takeLatest(types.SAVE_ORDER_SPLIT_OPPORTUNITY, saveSplitOpportunitySaga);
  yield takeLatest(types.UNDO_PROCESS_SPLIT_ORDER, undoProcessSplitOrderSaga);
  yield takeLatest(types.SEARCH_PROVISIONING_ATTRIBUTES_CONFIG, searchProvisioningAttributesConfigSaga);
  yield takeLatest(types.CREATE_PROVISIONING_ATTRIBUTES_CONFIG, createProvisioningAttributesConfigSaga);
  yield takeLatest(types.MODIFY_PROVISIONING_ATTRIBUTES_CONFIG, modifyProvisioningAttributesConfigSaga);
  yield takeLatest(types.BUILD_ORDER_PROVISIONING_ATTRIBUTES, buildOrderProvisioningAttributesSaga);
  yield takeLatest(types.GET_GATEWAY_API_MAP_BY_NAME_CONFIG, getGatewayApiMapByNameSaga);
  yield takeLatest(types.GET_ORDER_OMS_TASKS, getOrderOmsTasksSaga);
  yield takeLatest(types.GET_ORDER_PROVISIONING_OUTBOUND_TASKS, getOrderProvisioningOutboundTasksSaga);
  yield takeLatest(types.GET_ORDER_PROVISIONING_INBOUND_TASKS, getOrderProvisioningInboundTasksSaga);
  yield takeLatest(types.GET_ORDER_AUDIT, getOrderAuditSaga);
  yield takeLatest(types.CONVERT_JSON_TO_ORDER, convertJsonToOrderSaga);
}
